// Utility helpers for hallway_seat logic and row alignment
import React from 'react';

/**
 * Checks if a space is a hallway seat.
 * @param {Object} space - The space object to check.
 * @returns {boolean} True if the space is a hallway seat.
 */
export const isHallwaySeat = (space) =>
  space?.category === 'hallway_seat' || space?.type === 'hallway_seat';

/**
 * Checks if a row contains any hallway seats.
 * @param {Array} row - The row array to check.
 * @returns {boolean} True if the row contains hallway seats.
 */
export const hasHallwaySeatInRow = (row) => Array.isArray(row) && row.some(isHallwaySeat);

/**
 * Checks if a deck contains any hallway seats.
 * @param {Array} deck - The deck array to check.
 * @returns {boolean} True if the deck contains hallway seats.
 */
export const hasHallwaySeatInDeck = (deck) =>
  Array.isArray(deck) && deck.some((row) => hasHallwaySeatInRow(row));

/**
 * Gets the central aisle index based on diagram type.
 * @param {string} diagramType - The diagram type ('van' or other).
 * @returns {number} The central aisle index.
 */
export const getCentralAisleIndex = (diagramType) => (diagramType === 'van' ? 5 : 3);

/**
 * Creates an extra middle element for hallway seat alignment.
 * @param {string} key - The React key for the element.
 * @returns {JSX.Element} The middle element component.
 */
export const createExtraMiddleElement = (key) => (
  <div key={key} className="seats-layout-item-middle">
    <i className="hallway" />
  </div>
);

/**
 * Adjusts a row for hallway seat alignment by inserting extra elements.
 * @param {Array} deck - The deck containing all rows.
 * @param {Array} row - The current row being processed.
 * @param {number} rIndex - The row index.
 * @param {Array} originalRow - The original row elements.
 * @param {Object} [options={}] - Configuration options.
 * @param {boolean} [options.enabled=true] - Whether hallway seat alignment is enabled.
 * @param {string} [options.diagramType='bus'] - The diagram type.
 * @returns {Array} The adjusted row elements.
 */
export const adjustRowForHallwaySeat = (deck, row, rIndex, originalRow, options = {}) => {
  const { enabled = true, diagramType = 'bus' } = options;
  if (!Array.isArray(originalRow)) return originalRow;
  if (!enabled) return originalRow;
  if (!Array.isArray(deck) || !Array.isArray(row)) return originalRow;
  if (diagramType === 'van') return originalRow;

  const deckHas = hasHallwaySeatInDeck(deck);
  if (!deckHas) return originalRow;

  const rowHas = hasHallwaySeatInRow(row);
  if (rowHas) return originalRow;

  const elements = [...originalRow];
  const centralIndex = getCentralAisleIndex(diagramType);

  elements.splice(
    centralIndex,
    0,
    createExtraMiddleElement(`bd-bs-extra-${rIndex}-before-${centralIndex}`),
  );
  elements.splice(
    centralIndex + 2,
    0,
    createExtraMiddleElement(`bd-bs-extra-${rIndex}-after-${centralIndex}`),
  );

  return elements;
};

/**
 * Computes hallway metadata for a deck.
 * @param {Array} deck - The deck to analyze.
 * @param {Object} options - Configuration options.
 * @param {boolean} options.enabled - Whether hallway seat detection is enabled.
 * @returns {Object} Metadata about hallway seats in the deck.
 */
export const computeHallwayMeta = (deck, options = { enabled: true }) => {
  const enabled =
    options && Object.prototype.hasOwnProperty.call(options, 'enabled') ? options.enabled : true;
  if (!Array.isArray(deck)) {
    return { deckHasHallway: false, rowsHasHallway: [] };
  }
  if (!enabled) {
    return { deckHasHallway: false, rowsHasHallway: deck.map(() => false) };
  }
  const rowsHasHallway = deck.map((row) => hasHallwaySeatInRow(row));
  const deckHasHallway = rowsHasHallway.some((v) => v);
  return { deckHasHallway, rowsHasHallway };
};
